<template>
  <Transition name="ai-notice-fade">
    <aside
      v-if="visible"
      class="ai-disclosure-notice"
      role="status"
      aria-live="polite"
      :aria-label="$t('aiDisclosure.ariaLabel')"
    >
      <span class="ai-disclosure-icon" aria-hidden="true">🤖</span>
      <p class="ai-disclosure-text">
        {{ $t('aiDisclosure.chatNotice') }}
        <a href="/ki-transparenz" class="ai-disclosure-link">{{ $t('aiDisclosure.learnMore') }}</a>
      </p>
      <button
        type="button"
        class="ai-disclosure-dismiss"
        :aria-label="$t('aiDisclosure.dismiss')"
        @click="dismiss"
      >
        ×
      </button>
    </aside>
  </Transition>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { hasCommunicationConsent } from '../utils/analyticsConsent.js'

const STORAGE_KEY = 'ai_chat_disclosure_dismissed'
const visible = ref(false)

function shouldShow() {
  if (sessionStorage.getItem(STORAGE_KEY) === '1') {
    return false
  }
  return hasCommunicationConsent()
}

function showNotice() {
  if (shouldShow()) {
    visible.value = true
  }
}

function dismiss() {
  sessionStorage.setItem(STORAGE_KEY, '1')
  visible.value = false
}

onMounted(() => {
  showNotice()
  window.addEventListener('communication-consent-granted', showNotice)
})

onUnmounted(() => {
  window.removeEventListener('communication-consent-granted', showNotice)
})
</script>

<style scoped>
.ai-disclosure-notice {
  position: fixed;
  right: 16px;
  bottom: 88px;
  z-index: 9998;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: min(340px, calc(100vw - 32px));
  padding: 12px 14px;
  background: rgba(15, 16, 35, 0.96);
  border: 1px solid rgba(0, 255, 179, 0.45);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
}

.ai-disclosure-icon {
  font-size: 1.1rem;
  line-height: 1.4;
  flex-shrink: 0;
}

.ai-disclosure-text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #e2e8f0;
}

.ai-disclosure-link {
  color: #00ffb3;
  text-decoration: underline;
  white-space: nowrap;
}

.ai-disclosure-dismiss {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;
}

.ai-disclosure-dismiss:hover {
  background: rgba(255, 255, 255, 0.16);
}

.ai-notice-fade-enter-active,
.ai-notice-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.ai-notice-fade-enter-from,
.ai-notice-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 768px) {
  .ai-disclosure-notice {
    right: 12px;
    left: 12px;
    bottom: 76px;
    max-width: none;
  }
}
</style>
